import React from "react"
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks';
import { Accordion } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import { useLanguage } from "pages/dimension/LanguageContext";

import './Markdown.css'

type MarkdownSection = {
    header: string;
    content: string;
};

function splitMarkdownByH1(markdown: string): MarkdownSection[] {
    const sections: MarkdownSection[] = [];
    const lines = markdown.split('\n');
  
    let currentHeader = '';
    let currentContent = '';
  
    for (let line of lines) {
      if (line.startsWith('# ')) {
        if (currentHeader || currentContent) {
          sections.push({ header: currentHeader, content: currentContent.trim() });
          currentContent = '';
        }
        currentHeader = line.replace('# ', '');
      } else {
        currentContent += `\n${line}`;
      }
    }
  
    if (currentHeader || currentContent) {
      sections.push({ header: currentHeader, content: currentContent.trim() });
    }
  
    return sections;
}

const parseCustomParams = (altText: string) => {
    const params: Record<string, string> = {};
    //const regex = /(\w+)=([\w\d]+)/g;
    const regex = /(\w+)=["']?([^"'\s]+)["']?/g;
    let match;
    while ((match = regex.exec(altText)) !== null) {
      params[match[1]] = match[2];
    }
    return params;
  };

interface YouTubeProps {
    url: string;
    width?: string;
    height?: string;
}

const YouTube: React.FC<YouTubeProps> = ({ url, width, height}) => (
    <iframe
        width={width}
        height={height}
        src={url}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
    />
);

function getBasePath(url: string): string {
    const parsedUrl = new URL(url);
    return parsedUrl.origin + parsedUrl.pathname.substring(0, parsedUrl.pathname.lastIndexOf('/'));
}


export const StaticMarkdown: React.FC<{content: string, basePath?: string}> = (props) => {
    const data = splitMarkdownByH1(props.content)

    const transformImageUri = (localURL: string) => {
        const result = localURL.startsWith('http') || !props.basePath ? localURL : `${props.basePath}/${localURL}`
        console.log(`Markdown URL: ${localURL} => ${result}`)
        return result
    }

    const components = {
        a: ({ href, children }: { href?: string; children: React.ReactNode }) => {
            if (Array.isArray(children) && typeof children[0] === 'string' && children[0].startsWith('youtube') && href) {
                const params = parseCustomParams(children[0]);
                return <YouTube url={href} width={params.width} height={params.height} />;
              }
            return <a href={href} target="_blank">{children}</a>; // Regular link behavior otherwise
        }
    };

    return (
        <div className="markdown">
            {data.map((section, index) => (
                section.header.length == 0 &&
                <ReactMarkdown
                    children={section.content}
                    remarkPlugins={[remarkBreaks]}
                    transformImageUri={transformImageUri}
                    components={components}
                    key={index}
                />
            ))}
            <Accordion defaultActiveKey="0" alwaysOpen>
                {data.map((section, index) => (
                    section.header.length > 0 &&
                    <Accordion.Item eventKey={`${index}`} key={index}>
                        <Accordion.Header>{section.header}</Accordion.Header>
                        <Accordion.Body>
                            <ReactMarkdown
                                children={section.content}
                                remarkPlugins={[remarkBreaks]}
                                transformImageUri={transformImageUri}
                                components={components}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
        </div>
    )
};

export const Markdown: React.FC<{content?: string, url?: string, noCache?: boolean}> = (props) => {
    const basePath = props.url ? getBasePath(props.url) : '';
    const [content, setContent] = React.useState<string | undefined>(props.content)
    const langCtx = useLanguage();

    const loadContent = async (url: string) => {
        const response = await langCtx.fetchLocalized(url, props.noCache ? {cache: "no-cache"} : {});
        setContent(await response.text());
    }

    React.useEffect(() => {
        if (props.url) {
            loadContent(props.url)
        }
    }, [langCtx.language])

    if (!content) {
        return null;
    }

    return <StaticMarkdown content={content} basePath={basePath}></StaticMarkdown>
};

export function renderMarkdownHtml(content: string, basePath?: string) {
    return ReactDOMServer.renderToStaticMarkup(
        <StaticMarkdown content={content} basePath={basePath}/>
    );
}
